import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import BunqQrCode from "@/components/BunqQrCode";

function CopyRow({ label, value, testid, copyLabel, copiedLabel, copiedToast }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success(copiedToast || `${label} copied`);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="grid grid-cols-3 gap-4 items-center py-3 border-b border-neutral-300">
      <p className="font-mono-label text-[10px] text-neutral-500">{label}</p>
      <p data-testid={testid} className="col-span-2 font-mono text-base md:text-lg flex items-center justify-between gap-3">
        <span className="truncate">{value}</span>
        <button
          onClick={copy}
          className="font-mono-label text-[10px] tracking-[0.2em] text-black hover:underline underline-offset-4 flex-shrink-0"
        >
          {copied ? copiedLabel : copyLabel}
        </button>
      </p>
    </div>
  );
}

export default function BankInstructions() {
  const { ref } = useParams();
  const { lang } = useI18n();
  const [order, setOrder] = useState(null);
  const [bank, setBank] = useState(null);

  const nl = lang === "nl";
  const de = lang === "de";
  const L = {
    orderNotFoundLabel: nl ? "ORDER NIET GEVONDEN" : de ? "BESTELLUNG NICHT GEFUNDEN" : "ORDER NOT FOUND",
    orderNotFoundTitle: nl ? "Kenmerk onbekend" : de ? "Referenz unbekannt" : "Reference unknown",
    backToShop: nl ? "Terug naar shop" : de ? "Zurück zum Shop" : "Back to shop",
    loading: nl ? "LADEN…" : de ? "LÄDT…" : "LOADING…",
    payInstructions: nl ? "BETAALINSTRUCTIES" : de ? "ZAHLUNGSANWEISUNGEN" : "PAYMENT INSTRUCTIONS",
    almostDone: nl ? "Bijna klaar." : de ? "Fast fertig." : "Almost done.",
    transferSub: nl
      ? "Maak het bedrag over — dan versturen we binnen 24u."
      : de
      ? "Überweisen Sie den Betrag — dann versenden wir innerhalb 24 Std."
      : "Transfer the amount — we ship within 24 hours.",
    toPay: nl ? "TE BETALEN" : de ? "ZU ZAHLEN" : "TO PAY",
    bankDetails: nl ? "BETAALGEGEVENS" : de ? "ZAHLUNGSDATEN" : "BANK DETAILS",
    amount: nl ? "Bedrag" : de ? "Betrag" : "Amount",
    iban: "IBAN",
    bic: "BIC",
    holder: nl ? "Ten name van" : de ? "Empfänger" : "Account holder",
    reference: nl ? "Kenmerk *" : de ? "Verwendungszweck *" : "Reference *",
    refNote: nl
      ? "* Vermeld altijd dit kenmerk bij de overschrijving — zodat we jouw bestelling kunnen matchen."
      : de
      ? "* Bitte immer diesen Verwendungszweck angeben — damit wir Ihre Bestellung zuordnen können."
      : "* Always include this reference with your transfer — so we can match your order.",
    yourOrder: nl ? "JOUW BESTELLING" : de ? "IHRE BESTELLUNG" : "YOUR ORDER",
    total: nl ? "Totaal" : de ? "Gesamt" : "Total",
    copy: nl ? "KOPIEER" : de ? "KOPIEREN" : "COPY",
    copied: nl ? "GEKOPIEERD ✓" : de ? "KOPIERT ✓" : "COPIED ✓",
    refCopied: nl ? "Kenmerk gekopieerd" : de ? "Referenz kopiert" : "Reference copied",
    steps: nl
      ? [
          ["01", "Maak over", "Open je bank app (iDEAL / Bunq / SEPA) en betaal via IBAN + kenmerk."],
          ["02", "Wij checken", "Zodra we de betaling zien, krijg je een bevestigings-e-mail."],
          ["03", "Verzending 24u", "Bestelling wordt discreet verzonden vanuit Nederland."],
        ]
      : de
      ? [
          ["01", "Überweisen", "Öffnen Sie Ihre Banking-App (iDEAL / Bunq / SEPA) und zahlen Sie via IBAN + Verwendungszweck."],
          ["02", "Wir prüfen", "Sobald wir die Zahlung sehen, erhalten Sie eine Bestätigungs-E-Mail."],
          ["03", "Versand 24 Std.", "Bestellung wird diskret aus den Niederlanden versendet."],
        ]
      : [
          ["01", "Transfer", "Open your banking app (iDEAL / Bunq / SEPA) and pay via IBAN + reference."],
          ["02", "We verify", "Once we see the payment, you'll receive a confirmation email."],
          ["03", "Ships in 24h", "Order is dispatched discreetly from the Netherlands."],
        ],
    continueShopping: nl ? "Verder winkelen" : de ? "Weiter einkaufen" : "Continue shopping",
    mailUs: nl ? "Vraag? Mail ons →" : de ? "Frage? E-Mail an uns →" : "Question? Email us →",
    mailSubject: nl ? "Vraag over order" : de ? "Frage zur Bestellung" : "Question about order",
    emailAlso: (email) => nl
      ? `Je ontvangt de instructies ook per e-mail op ${email}`
      : de
      ? `Sie erhalten die Anweisungen auch per E-Mail an ${email}`
      : `You'll also receive these instructions via email at ${email}`,
  };

  useEffect(() => {
    api.get(`/orders/reference/${ref}`).then((r) => setOrder(r.data)).catch(() => setOrder(false));
    api.get(`/payment/bank-info`).then((r) => setBank(r.data)).catch(() => {});
  }, [ref]);

  if (order === false) {
    return (
      <div className="pt-40 pb-24 text-center bg-[#F7F7F6] min-h-screen">
        <p className="font-mono-label text-[11px] text-neutral-500 mb-4">[ {L.orderNotFoundLabel} ]</p>
        <h1 className="font-serif-display text-5xl mb-6">{L.orderNotFoundTitle}</h1>
        <Link to="/shop" className="btn-sharp inline-flex">{L.backToShop}</Link>
      </div>
    );
  }

  if (!order || !bank) {
    return <div className="pt-40 text-center font-mono-label text-xs">{L.loading}</div>;
  }

  return (
    <div data-testid="bank-instructions-page" className="pt-32 pb-24 bg-[#F7F7F6] min-h-screen">
      <div className="max-w-[900px] mx-auto px-6 md:px-12">
        <p className="font-mono-label text-[11px] text-neutral-500 mb-8">[ {L.payInstructions} · ORDER {order.reference} ]</p>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="font-serif-display font-light text-5xl md:text-7xl leading-[0.98] mb-4"
        >
          {L.almostDone}
        </motion.h1>
        <p className="font-serif-display italic text-2xl md:text-3xl text-neutral-500 mb-10">
          {L.transferSub}
        </p>

        {/* Amount hero */}
        <div className="relative overflow-hidden border-2 border-black bg-white p-8 md:p-12 mb-8">
          <div className="absolute -top-20 -left-20 w-[400px] h-[400px] pointer-events-none" style={{ background: "radial-gradient(circle at center, rgba(0,82,155,0.14), transparent 65%)", filter: "blur(50px)" }} />
          <div className="relative">
            <p className="font-mono-label text-[10px] text-neutral-500">[ {L.toPay} ]</p>
            <p data-testid="pay-amount" className="font-serif-display text-7xl md:text-8xl leading-none mt-2">€{Number(order.total).toFixed(2)}</p>
          </div>
        </div>

        {/* Bunq / SEPA QR code — scan & pay */}
        <BunqQrCode
          amount={order.total}
          reference={order.reference}
          bank={bank}
        />

        {/* Bank details block */}
        <div className="border border-neutral-300 bg-white p-6 md:p-10">
          <p className="font-mono-label text-[10px] text-[#00529B] mb-4">[ {L.bankDetails} ]</p>
          <CopyRow label={L.amount} value={`€${Number(order.total).toFixed(2)}`} testid="row-amount" copyLabel={L.copy} copiedLabel={L.copied} copiedToast={`${L.amount} ✓`} />
          <CopyRow label={L.iban} value={bank.iban} testid="row-iban" copyLabel={L.copy} copiedLabel={L.copied} copiedToast={`${L.iban} ✓`} />
          <CopyRow label={L.bic} value={bank.bic} testid="row-bic" copyLabel={L.copy} copiedLabel={L.copied} copiedToast={`${L.bic} ✓`} />
          <CopyRow label={L.holder} value={bank.account_holder} testid="row-holder" copyLabel={L.copy} copiedLabel={L.copied} copiedToast={`${L.holder} ✓`} />
          <div className="grid grid-cols-3 gap-4 items-center py-3">
            <p className="font-mono-label text-[10px] text-neutral-500">{L.reference}</p>
            <p className="col-span-2 font-mono text-lg md:text-xl flex items-center justify-between gap-3">
              <span data-testid="row-ref" className="text-[#00529B] font-semibold">{order.reference}</span>
              <button
                onClick={async () => { await navigator.clipboard.writeText(order.reference); toast.success(L.refCopied); }}
                className="font-mono-label text-[10px] tracking-[0.2em] text-black hover:underline underline-offset-4"
              >
                {L.copy}
              </button>
            </p>
          </div>
          <p className="mt-3 font-mono-label text-[10px] text-neutral-500">
            {L.refNote}
          </p>
        </div>

        {/* Order recap */}
        <div className="border border-neutral-300 bg-white p-6 md:p-8 mt-6">
          <p className="font-mono-label text-[10px] text-neutral-500 mb-4">[ {L.yourOrder} ]</p>
          {order.items.map((it, i) => (
            <div key={i} className="flex justify-between border-t border-neutral-200 py-2">
              <span>{it.name} · {it.dosage} × {it.quantity}</span>
              <span className="font-serif-display">€{Number(it.line_total).toFixed(2)}</span>
            </div>
          ))}
          <div className="flex justify-between mt-3 pt-3 border-t border-black">
            <span className="font-serif-display text-xl">{L.total}</span>
            <span className="font-serif-display text-xl">€{Number(order.total).toFixed(2)}</span>
          </div>
        </div>

        {/* Next steps */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          {L.steps.map(([n, title, body]) => (
            <div key={n} className="border-t border-neutral-400 pt-3">
              <p className="font-mono-label text-[10px] text-neutral-500">{n}</p>
              <p className="font-serif-display text-xl mt-1">{title}</p>
              <p className="text-sm text-neutral-600 mt-2 leading-snug">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/shop" className="btn-sharp">{L.continueShopping}</Link>
          <a
            href={`mailto:hello@peptivance.eu?subject=${encodeURIComponent(L.mailSubject)}%20${order.reference}`}
            className="btn-solid"
          >
            {L.mailUs}
          </a>
        </div>

        <p className="mt-10 font-mono-label text-[9px] text-neutral-500">
          {L.emailAlso(order.shipping_email)}
        </p>
      </div>
    </div>
  );
}
